import { useEffect, useState } from "react";
import {
  getActiveSettlements,
  getFinishedSettlements,
} from "../../setup/api/services/settlements";
import {
  Box,
  CircularProgress,
  Container,
  Pagination,
  PaginationItem,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import styles from "./settlements.module.scss";
import {
  ACTIVE_SETTLEMENTS_TAB,
  BRIEF,
  BUTTON,
  FINISHED_SETTLEMENTS_TAB,
  HEADER,
} from "./constants";
import SearchInput from "../../components/common/searchInput";
import ContractsListItem from "../../components/contractListItem";
import { AuthConfig } from "../../setup/context/config/authConfig";
import { EmptyContractsList } from "../contract/list/emptyContractList";
import { SingleValue } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { getAllOrganizations } from "../../setup/api/services/organization";
import { IOrganizations } from "../../setup/types/Organisation";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;

const Settlements = () => {
  const { t } = useTranslation();
  const [settlements, setSettlements] = useState([]);
  const [tabType, setTabType] = useState("active");
  const [searchValue, setSearchValue] = useState("");
  const [transactionSearchValue, setTransactionSearchValue] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [organization, setOrganization] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const handleOrganizationSelected = (
    newValue: SingleValue<{
      value: string;
      label: string;
    }>,
    _
  ) => {
    setOrganization(newValue);
    setPage(1);
  };

  const fetchActiveSettlements = async () => {
    setLoading(true);
    try {
      const body = {
        referenceTransactionId: transactionSearchValue,
        contractId: searchValue,
        page: page - 1,
        size: PAGE_SIZE,
        organizationCode: organization?.value,
      };
      const response = await getActiveSettlements(body);
      setSettlements(response.data.content);
      setPagesCount(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setSettlements([]);
    }
  };

  const fetchFinishedSettlements = async () => {
    setLoading(true);
    try {
      const body = {
        referenceTransactionId: transactionSearchValue,
        contractId: searchValue,
        page: page - 1,
        size: PAGE_SIZE,
        organizationCode: organization?.value,
      };
      const response = await getFinishedSettlements(body);
      setSettlements(response.data.content);
      setPagesCount(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setSettlements([]);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabType(newValue);
    setPage(1);
  };

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    if (tabType === "active") {
      fetchActiveSettlements();
    } else {
      fetchFinishedSettlements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabType, searchValue, transactionSearchValue, page, organization]);

  const loadOptions = async (q: string, _, additional) => {
    const waffyOption = {
      value: "WAFFY",
      label: "WAFFY",
    };
    const response = await getAllOrganizations(additional.page, PAGE_SIZE, q);
    const options = response.data.content.map((org: IOrganizations) => ({
      value: org.code,
      label: org.name,
    }));

    if (additional.page === 0) {
      options.unshift(waffyOption);
    }

    return {
      options: options,
      hasMore: response.data.totalPages > additional.page + 1,
      additional: {
        page: additional.page + 1,
      },
    };
  };

  return (
    <div className={styles.container}>
      <Container maxWidth="xl">
        <Box justifyContent="right">
          <Typography
            gutterBottom
            variant="h6"
            sx={{
              fontWeight: "700",
              fontSize: "20px",
              color: "#283743",
              padding: "1em 0 0 0",
            }}>
            {HEADER}
          </Typography>
          <Typography
            gutterBottom
            sx={{ color: "#58646D", fontWeight: "400", fontSize: "1em" }}>
            {BRIEF}
          </Typography>
        </Box>
        <div>
          <div className={styles.header_container}>
            <div className={styles["right_section"]}>
              <Tabs
                style={{ alignItems: "center" }}
                className={styles.tab_wrapper}
                value={tabType}
                onChange={handleTabChange}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "#078DE7",
                  },
                }}>
                <Tab
                  value="active"
                  label={ACTIVE_SETTLEMENTS_TAB}
                  sx={{
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                    fontSize: { xs: "0.75em", md: "1em" },
                    color:
                      tabType === "active" ? "#078DE7 !important" : undefined,
                  }}
                />
                <Tab
                  value="finished"
                  label={FINISHED_SETTLEMENTS_TAB}
                  sx={{
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                    fontSize: { xs: "0.75em", md: "1em" },
                    color:
                      tabType === "finished" ? "#078DE7 !important" : undefined,
                  }}
                />
              </Tabs>
              <div style={{ marginLeft: "8px" }}>
                <AsyncPaginate
                  additional={{ page: 0 }}
                  value={organization}
                  loadOptions={loadOptions}
                  onChange={handleOrganizationSelected}
                  placeholder={t("lbl_choose_organisation")}
                  styles={{
                    control: (base) => ({
                      ...base,
                      width: 200,
                      height: "3rem",
                      padding: "0 1rem",
                      border: "1px solid #E2E8EE",
                      borderRadius: "6rem",
                    }),
                  }}
                />
              </div>
            </div>
            <div className={styles["left_section"]}>
              <SearchInput
                dir="ltr"
                placeholder={BUTTON.SEARCH}
                value={searchValue}
                style={{ height: "35px", width: "252px" }}
                handleChange={(event) => {
                  setSearchValue(event.target.value);
                }}
              />
              <SearchInput
                dir="ltr"
                placeholder={BUTTON.SEARCH_TRANS}
                value={transactionSearchValue}
                style={{ height: "35px", width: "252px" }}
                handleChange={(event) => {
                  setTransactionSearchValue(event.target.value);
                }}
              />
            </div>
          </div>
          <div className={classNames(styles.listSection)}>
            <Box
              className={classNames(styles.spinner)}
              sx={{ display: loading ? "flex" : "none" }}>
              <CircularProgress />
            </Box>
            {!loading && settlements.length ? (
              settlements.map((settlement: any) => {
                return (
                  <div className={styles.list_wrapper} key={settlement.id}>
                    <ContractsListItem
                      userId={AuthConfig.getUserInfo().id}
                      contract={settlement}
                      isCashoutRoute={tabType === "finished"}
                      isTabContractRoute={tabType === "active"}
                    />
                  </div>
                );
              })
            ) : (
              <Box
                className={classNames(styles.emptyListContainer)}
                sx={{ display: loading ? "none" : "flex" }}>
                <EmptyContractsList />
              </Box>
            )}
          </div>
        </div>
        <Box
          className={
            settlements.length ? styles.paginatorContainer : styles.hide
          }>
          <Pagination
            count={pagesCount}
            page={page}
            variant="outlined"
            shape="rounded"
            size="medium"
            onChange={handlePaginationChange}
            color="primary"
            siblingCount={1}
            boundaryCount={1}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  "&.MuiButtonBase-root.Mui-selected": {
                    background: "#078DE7",
                    color: "#FFFFFF",
                    borderColor: "transparent",
                    height: { xs: "2em", md: "3em" },
                    width: { xs: "2em", md: "3em" },
                  },
                  "&.MuiPaginationItem-root.MuiPaginationItem-previousNext": {
                    background: "#283743",
                    color: "#FFFFFF",
                    borderColor: "#283743",
                    fontSize: 16,
                  },
                  "&.MuiPaginationItem-root": {
                    color: "#078DE7",
                    borderColor: "#078DE7",
                    fontSize: 18,
                    fontWeight: 800,
                    height: { xs: "2em", md: "3em" },
                    width: { xs: "2em", md: "3em" },
                    borderRadius: "8px",
                  },
                }}
              />
            )}
          />
        </Box>
      </Container>
    </div>
  );
};

export default Settlements;
